/**
 * The application: sign-in gate, router, and the two pages inside the shell.
 *
 * This file used to hold the roadmap itself and a note saying a router would be
 * added if a second real page ever arrived. The Team page is that second page, so
 * here it is.
 *
 * Deep links work because CloudFront is configured to answer both 403 and 404 with
 * /index.html at status 200 (cdk/lib/frontend_stack.py) - S3 returns 403, not 404,
 * for a key that does not exist when the bucket is private, so mapping only 404
 * would leave /team as an access-denied page for anybody who typed it or reloaded on
 * it. Verified against the deployed site, not just reasoned about.
 *
 * BrowserRouter rather than HashRouter for the same reason: /team is an address
 * somebody can be sent, and #/team in an email looks like a broken link.
 *
 * The layout route means AppShell mounts once and stays mounted while the tabs
 * change, so /api/me is called once per session rather than once per navigation.
 * Page state is deliberately NOT hoisted with it - switching tabs refetches the
 * roadmap, which is the correct behaviour for a board several people are editing at
 * the same time.
 *
 * THE PAGES ARE LAZY, AND THE RFC PAGES ARE WHY
 * ---------------------------------------------
 * Markdown rendering is a large dependency - the parser, the AST, the GitHub
 * extensions - and it is needed by exactly one route. Imported statically it lands in
 * the single bundle that everybody downloads before the roadmap can paint, so the
 * people who never open an RFC pay for it on every cold load.
 *
 * React.lazy puts each page in its own chunk, and Vite follows the import graph, so
 * the markdown code follows RfcPage into a chunk that is fetched the first time
 * somebody navigates there. The measured effect was worth doing before adding the
 * dependency rather than after: it is much easier to keep a bundle split than to
 * split one that has already grown.
 *
 * AppShell and LoginGate stay STATIC on purpose. Both are on the path to every route
 * - lazy-loading them would add a network round trip before anything at all can
 * render, in exchange for splitting out two files that are needed immediately.
 *
 * The Suspense boundary lives inside AppShell rather than here, wrapped around the
 * outlet it already renders. That is not a stylistic choice: AppShell passes the
 * identity down through `<Outlet context={identity} />`, and a second, pathless
 * <Route> holding its own <Outlet /> would provide `undefined` over the top of it -
 * react-router's useOutlet always installs a provider, with whatever it was given.
 * Every page's useIdentity() would then read null, and the Team page would quietly
 * stop drawing its admin controls with nothing on screen to explain why.
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import AppShell from './components/AppShell';
import LoginGate from './components/LoginGate';
import { lazyChunk } from './services/chunks';
import { GlobalStyle } from './styles/ui';

const RoadmapPage = lazyChunk(() => import('./pages/RoadmapPage'));
const TeamPage = lazyChunk(() => import('./pages/TeamPage'));
const RfcsPage = lazyChunk(() => import('./pages/RfcsPage'));
const RfcPage = lazyChunk(() => import('./pages/RfcPage'));
const TasksPage = lazyChunk(() => import('./pages/TasksPage'));
const TaskPage = lazyChunk(() => import('./pages/TaskPage'));
const MinePage = lazyChunk(() => import('./pages/MinePage'));
const BugsPage = lazyChunk(() => import('./pages/BugsPage'));
const BugPage = lazyChunk(() => import('./pages/BugPage'));
const FinancePage = lazyChunk(() => import('./pages/FinancePage'));
const GuidesPage = lazyChunk(() => import('./pages/GuidesPage'));
const SettingsPage = lazyChunk(() => import('./pages/SettingsPage'));
const MilestoneLogPage = lazyChunk(() => import('./pages/MilestoneLogPage'));
const WireframesPage = lazyChunk(() => import('./pages/WireframesPage'));
const ActivityPage = lazyChunk(() => import('./pages/ActivityPage'));
const ManageProjectsPage = lazyChunk(() => import('./pages/ManageProjectsPage'));
const ShiftSchedulePage = lazyChunk(() => import('./pages/ShiftSchedulePage'));
const MarkPeriodsPage = lazyChunk(() => import('./pages/MarkPeriodsPage'));

export default function App() {
  return (
    <>
      <GlobalStyle />
      <LoginGate>
        {(auth) => (
          <BrowserRouter>
            <Routes>
              <Route element={<AppShell auth={auth} />}>
                <Route index element={<RoadmapPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="rfcs" element={<RfcsPage />} />
                {/* The reader and the editor are one route: /rfcs/new is the editor
                    with nothing loaded, which keeps "write an RFC" a real address
                    somebody can be sent rather than a mode you can only reach by
                    clicking. */}
                <Route path="rfcs/:itemId" element={<RfcPage />} />
                <Route path="tasks" element={<TasksPage />} />
                {/* One person's own work, gathered from four places. Its own address
                    rather than a filter on the board, because it answers a different
                    question - "what have I got" rather than "what is on this project" -
                    and because it is a page somebody keeps open. No id in the path: it
                    reads the signed-in address from the shell's identity, and
                    /mine/{email} would imply somebody else's is readable here. */}
                <Route path="mine" element={<MinePage />} />
                {/* The bug queue. Its own address rather than a corner of the board,
                    because the people who file here mostly do not use the board: the
                    board is arranged by project and status for people who already know
                    how the work is organised, and asking somebody who has just come
                    off a call to pick a phase is asking them to file rather than to
                    report. */}
                <Route path="bugs" element={<BugsPage />} />
                {/* One bug, with its pull requests and its thread. A real address like
                    the RFC and task pages, because "have a look at this one" is a
                    message people send and a row you have to scroll to find is one
                    nobody sends. */}
                <Route path="bugs/:itemId" element={<BugPage />} />
                {/* How the app works, kept. Two routes on one component: the index and
                    a guide are the same page at this size, and a separate index would
                    be an extra click to reach six sentences. Each guide has its own
                    address because "read the bugs guide" is a link people paste. */}
                {/* Top level rather than under Settings: it is a report, not a
                    configuration, and the people it is for do not think of
                    themselves as administering anything. The tab is drawn for
                    the finance role, admins and finance leads - but the ROUTE
                    is open, because the report is open. Hiding a screen whose
                    every fact is on the front page would be theatre. */}
                <Route path="finance" element={<FinancePage />} />
                <Route path="guides" element={<GuidesPage />} />
                <Route path="guides/:slug" element={<GuidesPage />} />
                {/* Same shape as the RFC pair, and for the same reason: /tasks/new is
                    the editor with nothing loaded, so "add a task" is an address
                    somebody can be sent. The `new` sentinel cannot collide with a
                    real id because ids carry a `tsk_` prefix. */}
                <Route path="tasks/:itemId" element={<TaskPage />} />
                {/* One person's own preferences, so there is no id in the path - the
                    page reads the signed-in address from the identity the shell already
                    fetched. /settings/{email} would imply somebody else's are editable
                    here, and they are not. */}
                <Route path="settings" element={<SettingsPage />} />
                {/* The admin activity log, UNDER /settings rather than at /activity.
                    The address is what puts it in the Settings section: the shell
                    decides which sub-navigation to draw from the path prefix, so a
                    top-level /activity would open with the Projects row above it,
                    offering to take you back out of the page you just chose. */}
                <Route path="settings/activity" element={<ActivityPage />} />
                {/* Archiving and deleting lanes and groups. UNDER SETTINGS rather than
                    on the roadmap: the roadmap is a working surface people click around
                    all day, and a control that takes a lane away from the whole team
                    should not sit one slip of the mouse from the controls that move a
                    date. Unguarded here like the milestone log - the page explains the
                    refusal, and the server enforces it. */}
                <Route path="settings/projects" element={<ManageProjectsPage />} />
                <Route path="settings/schedule" element={<ShiftSchedulePage />} />
                {/* Marking a firm-wide period. Beside the shift because they are the
                    same family of decision, and gated more widely than it: drawing a
                    band moves nothing, so planning leads have it too. Unguarded here
                    like the rest - the page explains the refusal, and the API is the
                    boundary. */}
                <Route path="settings/periods" element={<MarkPeriodsPage />} />
                {/* The milestone log. A real route rather than a panel on another
                    page, because it is a thing somebody links to - "see the log, this
                    is the third time" is a message people send. Unguarded here on
                    purpose: the page itself explains the refusal, and a route that
                    silently redirected would leave somebody who typed the address with
                    no idea why they are looking at the roadmap. */}
                <Route path="milestone-log" element={<MilestoneLogPage />} />
                {/* The gallery. A real address because "look at the wireframes" is a
                    thing somebody says in Slack, and a tab you have to describe how to
                    reach is one nobody reaches. */}
                <Route path="wireframes" element={<WireframesPage />} />
                {/* Anything else goes to the roadmap rather than to a 404 page. The
                    only ways to reach an unknown path are a typo and a stale link,
                    and both are better served by the board than by an apology. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        )}
      </LoginGate>
    </>
  );
}
